import {useEffect, useEffectEvent, useRef} from 'react'
import {useChatStateLens, useChatStateValues} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {getSelectedThread} from '@github-ui/copilot-chat-state/get-selected-thread'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   The v1 conversation still reads the legacy app payload context. */
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import {useParams} from '@github-ui/react-router'
import {LAYOUT_BREAKPOINT} from '@github-ui/pacer/Layout'
import {useMediaQuery} from '@github-ui/use-media-query'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This adapter intentionally hosts the legacy v1 transcript until it has a Relay-free replacement.
import {ConversationView} from '@github-ui/copilot-immersive-v1/components/ConversationView'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This adapter intentionally hosts the legacy v1 transcript until it has a Relay-free replacement.
import {CopilotImmersiveProviders} from '@github-ui/copilot-immersive-v1/components/CopilotImmersiveProviders'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This adapter intentionally hosts the legacy v1 transcript until it has a Relay-free replacement.
import type {CopilotImmersivePayload} from '@github-ui/copilot-immersive-v1/routes/payloads'

export type ReadOnlyLegacyChatPayload = CopilotImmersivePayload

function LegacyThreadLoader({threadID}: {threadID: string}) {
  const manager = useChatManager()
  const {messagesLoading} = useChatStateValues('messagesLoading')
  const retryAfterSso = useEffectEvent(() => {
    if (messagesLoading.state === 'error' && messagesLoading.missingOrgIds?.length) {
      void manager.fetchMessages(threadID)
    }
  })

  useEffect(() => {
    void manager.fetchMessages(threadID)
  }, [manager, threadID])

  useEffect(() => {
    window.addEventListener('pageshow', retryAfterSso)
    return () => window.removeEventListener('pageshow', retryAfterSso)
  }, [])

  return null
}

function LegacyThreadTitleSync({
  threadID,
  onTitleChange,
}: {
  threadID: string
  onTitleChange: (value: {threadID: string; title: string}) => void
}) {
  const thread = useChatStateLens(getSelectedThread)
  const title = thread?.name || 'Legacy chat'
  const publishTitle = useEffectEvent(onTitleChange)

  useEffect(() => {
    publishTitle({threadID, title})
  }, [threadID, title])

  return null
}

export function ReadOnlyLegacyChat({
  payload,
  onTitleChange,
}: {
  payload: ReadOnlyLegacyChatPayload
  onTitleChange?: (value: {threadID: string; title: string}) => void
}) {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const isNarrowViewport = useMediaQuery(LAYOUT_BREAKPOINT)
  const {threadID} = useParams<{threadID: string}>()

  if (!threadID) return null

  return (
    <AppPayloadContext value={payload}>
      <CopilotImmersiveProviders key={threadID} payload={payload}>
        {onTitleChange && <LegacyThreadTitleSync threadID={threadID} onTitleChange={onTitleChange} />}
        <LegacyThreadLoader threadID={threadID} />
        <ConversationView isMobile={isNarrowViewport} textAreaRef={textAreaRef} readOnly />
      </CopilotImmersiveProviders>
    </AppPayloadContext>
  )
}
